import {CommonModule} from '@angular/common';
import {HttpProgressEvent, HttpResponse, HttpUploadProgressEvent} from '@angular/common/http';
import {ChangeDetectorRef, Component, inject, Input, OnInit} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSortModule} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ActivatedRoute} from '@angular/router';
import {FileUploadDialogComponent} from '@app/dialogs/file-upload-dialog/file-upload-dialog.component';
import {FileUploadFn, FileUploadManager} from '@app/shared/types/file-upload';
import {FileResponse, ResourceResponse} from '@middleware/models';
import {FileAttachment} from '@models/file-attachment';
import {FileUploadDialogData} from '@models/file-upload';
import {ResourcePermissionsFlags} from '@models/search';
import {DateFormatterService} from '@services/date/date-formatter.service';
import {FileService} from '@services/file/file.service';
import {formatSize} from '@utilities/format-size';
import {BehaviorSubject, EMPTY, filter, first, map, Observable} from 'rxjs';

export type uploadResourceAttachmentFn = (
  file: File,
  existingFiles: FileAttachment[],
) => Observable<HttpProgressEvent | HttpResponse<FileResponse>>;

@Component({
  standalone: true,
  selector: 'app-resource-attachments',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSnackBarModule,
    MatSortModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressBarModule,
  ],
  templateUrl: './resource-attachments.component.html',
  styleUrl: './resource-attachments.component.scss',
})
export class ResourceAttachmentsComponent implements OnInit {
  private readonly fileService = inject(FileService);
  private readonly route = inject(ActivatedRoute);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly snackBarService = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);

  @Input() resource!: ResourceResponse & ResourcePermissionsFlags;

  public readonly resourceSignal = toSignal(
    this.route.data.pipe(map(data => data['resource'] as ResourceResponse & ResourcePermissionsFlags)),
    {
      initialValue: this.resource,
    },
  );

  private readonly attachmentsSubject = new BehaviorSubject<FileAttachment[]>([]);
  public attachments$ = this.attachmentsSubject.asObservable();
  public dataSource = new MatTableDataSource<FileAttachment>([]);
  public displayedColumns: string[] = [];

  public canEdit = false;
  public isHovering = false;

  public readonly fileUploadFn!: FileUploadFn;

  ngOnInit(): void {
    const resource = this.resourceSignal();
    this.canEdit = resource?._can_edit === true;

    const initialFiles: FileResponse[] = resource?.files || this.resource?.files || [];
    this.attachmentsSubject.next(initialFiles as FileAttachment[]);

    this.setupTable();
    this.attachments$.subscribe(files => {
      this.dataSource.data = files;
      this.cdr.detectChanges();
    });
  }

  private setupTable(): void {
    this.displayedColumns = ['file_name', 'size', 'time_updated', 'download_action'];

    if (this.canEdit) {
      this.displayedColumns.push('delete_action');
    }

    this.dataSource.sortingDataAccessor = (item, headerId) => {
      switch (headerId) {
        case 'file_name':
          return item.file_name;
        case 'size':
          return item.size;
        case 'time_updated':
          return new Date(item.time_updated).getTime();
        default:
          return (item as any)[headerId];
      }
    };
  }

  public formatFileSize(value: number): string {
    return formatSize(value, 2);
  }

  public formatDateTime(value: string): string {
    return this.dateFormatter.formatDate(value) + ' ' + this.dateFormatter.formatTimeWithTimezone(value);
  }

  createFileUploadAdapter(): FileUploadFn {
    return (fileUploadManager: FileUploadManager) => {
      return this.fileService
        .uploadResourceAttachment(this.resource.id, this.attachmentsSubject.value, fileUploadManager.payload.file)
        .pipe(
          map(event => {
            if (event instanceof HttpResponse) {
              return event.body as FileResponse;
            }
            return event;
          }),
        ) as Observable<FileResponse | HttpUploadProgressEvent>;
    };
  }

  createDeleteAdapter(): (fileId: string) => Observable<unknown> {
    return (fileId: string): Observable<unknown> => {
      const resourceId = this.resourceSignal()?.id;

      if (!resourceId) {
        console.error('Resource ID is missing. Cannot delete file.');
        return EMPTY;
      }

      return this.fileService.deleteAttachment(resourceId, fileId);
    };
  }

  /**
   * Renamed to align with original component's click handler.
   * Opens the custom file upload dialog when the "Add Attachment" button is clicked.
   */
  public openFileUploadDialog(existingFiles?: FileResponse[]): void {
    if (!this.canEdit) {
      return;
    }

    const dialogData: FileUploadDialogData = {
      id: this.resource.id,
      fileUploadFn: this.createFileUploadAdapter(),
      fileResponses: existingFiles,
    };

    this.dialog
      .open(FileUploadDialogComponent, {
        data: dialogData,
        width: '500px',
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        filter(result => result && result.length > 0),
        map(results => results as FileAttachment[]),
        first(),
      )
      .subscribe(uploadedFiles => {
        const currentFiles = this.attachmentsSubject.value;
        const updatedFiles = [...currentFiles, ...uploadedFiles];
        this.attachmentsSubject.next(updatedFiles);
        this.snackBarService.open('File(s) uploaded successfully.', 'Close', {duration: 3000});
      });
  }

  /**
   * Initiates the download of a file by creating a temporary anchor tag and
   * navigating to the file's URL.
   */
  downloadFile(fileResponse: FileResponse): void {
    if (!fileResponse?.url) {
      console.error(`File URL not found for file ID: ${fileResponse?.id}`);
      return;
    }

    const link = document.createElement('a');
    link.href = fileResponse.url;
    link.download = fileResponse.file_name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deleteAttachment(fileResponse: FileAttachment): void {
    if (!fileResponse?.id) {
      console.error('Cannot delete file: ID is missing.');
      return;
    }

    const deleteFn = this.createDeleteAdapter();
    deleteFn(fileResponse.id).subscribe({
      next: () => {
        const currentFiles = this.attachmentsSubject.value;
        const updatedFiles = currentFiles.filter(f => f.id !== fileResponse.id);
        this.attachmentsSubject.next(updatedFiles);

        this.snackBarService.open('File deleted successfully.', 'Close', {duration: 3000});
      },
      error: err => {
        console.error('Error deleting file:', err);
        this.snackBarService.open('Failed to delete file.', 'Close', {duration: 3000});
      },
    });
  }
}
